import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

/** The ONE password input on the site, so the create-account flow at checkout, the
 *  register page and the reset-confirm page can never state different rules.
 *  The rule mirrors what the vendor actually enforces (probed: 12-128 characters,
 *  no composition requirement) and is shown up front, before submission. */
export const PASSWORD_MIN = 12;
export const PASSWORD_MAX = 128;
export const PASSWORD_RULE = `At least ${PASSWORD_MIN} characters. No symbols or capitals required — length is what matters.`;

export function passwordError(password, confirm) {
  if (password.length < PASSWORD_MIN) return `Please use at least ${PASSWORD_MIN} characters.`;
  if (password.length > PASSWORD_MAX) return `Please use ${PASSWORD_MAX} characters or fewer.`;
  if (confirm !== undefined && password !== confirm) return "Those passwords don't match.";
  return null;
}

export const PasswordField = ({
  id,
  testId,
  label,
  value,
  onChange,
  error,
  autoComplete = "new-password",
  hint,
  required = true,
}) => {
  const [shown, setShown] = useState(false);
  const errId = `${id}-error`;
  const hintId = `${id}-hint`;
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-bold uppercase tracking-wider text-slate-500">
        {label} {required && <span className="text-ember">*</span>}
      </label>
      <div className="relative mt-2">
        <input
          id={id}
          data-testid={testId}
          type={shown ? "text" : "password"}
          autoComplete={autoComplete}
          minLength={autoComplete === "new-password" ? PASSWORD_MIN : undefined}
          maxLength={PASSWORD_MAX}
          aria-invalid={Boolean(error)}
          aria-describedby={[hint ? hintId : null, error ? errId : null].filter(Boolean).join(" ") || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`h-12 w-full rounded-xl border px-3 pr-12 text-sm text-navy transition-colors focus:border-lake focus:outline-none focus:ring-2 focus:ring-lake/30 ${
            error ? "border-red-500 bg-red-50" : "border-sand-border bg-white"
          }`}
        />
        <button
          type="button"
          data-testid={`${testId}-toggle`}
          onClick={() => setShown((s) => !s)}
          aria-label={shown ? "Hide password" : "Show password"}
          aria-pressed={shown}
          className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-sand hover:text-navy focus:outline-none focus:ring-2 focus:ring-lake/40"
        >
          {shown ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>
      {hint && (
        <p id={hintId} data-testid={`${testId}-rule`} className="mt-1.5 text-xs text-slate-500">
          {hint}
        </p>
      )}
      {error && (
        <p id={errId} data-testid={`${testId}-error`} role="alert" aria-live="polite" className="mt-1 text-xs font-semibold text-red-600">
          {error}
        </p>
      )}
    </div>
  );
};

export default PasswordField;
